// §4.4 TRANSFER RECEIVE (floor) — spec 06 §3.3, §9.10.
//
// "Scan the transfer QR on the paper manifest or pick from Inbound to my
// facility → per-line scan each unit (green tick as scanned) → enter qtys for
// bulk lines → variance reason picker when short → Confirm. Works offline;
// confirms queue in outbox."
//
// The two rules the wire enforces and this screen therefore enforces at the
// input, so the operator learns them at the loading bay and not from a queued
// refusal an hour later:
//
//  * A SHORT LINE NEEDS A REASON. 422 `TRANSFER_VARIANCE_REASON_REQUIRED`
//    (§3.3). Confirm is blocked until every short line carries one, and the
//    reason picker appears the moment the entered quantity drops below the
//    dispatched one. §9.10: the shortfall STAYS in_transit — the screen says
//    that, because "50 kg short" and "50 kg lost" are different facts and only
//    the ledger decides which.
//  * A UNIT LINE RECEIVES IN FULL OR NOT AT ALL (ruled 2d — receive whole, then
//    re-measure). So a unit line is a TICK, not a number field. Offering a
//    quantity box on a bobbin would invite a partial that the server refuses.
//
// Over-receiving past a line's dispatched quantity is 422 `TRANSFER_STATE_INVALID`
// (found excess is a §3.9 adjustment, not a receive), so the input is capped and
// says why rather than queueing a body that cannot land.
//
// OFFLINE: the manifest is the cached snapshot from the last online read; the
// confirm queues. A transfer never partially applies from this screen — one
// confirm is one queued call covering every line the operator ticked.

import { useMemo, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  FloorButton,
  FloorEmpty,
  FloorError,
  FloorNeedsConnection,
  FloorNotSaved,
  FloorScreen,
  FloorSkeleton,
  SnapshotNote,
} from "../components/floor-chrome";
import { ScanInput } from "../components/scan-input";
import { floorReadSignal, useCachedRead } from "../hooks/use-cached-read";
import { useFloorSubmit } from "../hooks/use-floor-outbox";
import { useFloorDraft } from "../hooks/use-floor-draft";
import { readCacheKey } from "../lib/floor-read-cache";
import { floorFeedback } from "../lib/floor-feedback";
import { floorFailureMessage, floorRequestId } from "../lib/floor-errors";
import type { TransferReceiveRequest, TransferReceiveResponse } from "../lib/floor-ops";
import { useInventoryPermissions } from "../../office/inventory/hooks/permissions";
import { formatMeasure, parseMeasure } from "../../office/inventory/lib/measure";
import { client } from "../../../lib/api";
import { unwrap } from "../../../lib/request";
import { formatBusinessDate } from "../../../lib/datetime";
import type { components } from "@assessly/api-client";
import { unitSymbol } from "../../../lib/quantity";
import { enumLabel } from "../../../lib/enum-label";

type S = components["schemas"];
type TransferLine = S["TransferLine"];

const MEASURE_RE = /^\d{1,12}(\.\d{1,6})?$/u;

/** §3.3 variance reasons a receiving bay actually uses (free text on the wire). */
const VARIANCE_REASONS = ["spillage", "damage", "short_shipped", "miscount", "other"] as const;

// ---------------------------------------------------------------------------
// Inbound list
// ---------------------------------------------------------------------------

export function FloorReceiveListScreen() {
  const { t, i18n } = useTranslation();
  // Where a transfer comes from, by name (D-444 ruling 6). The facility list is
  // cached on the device like the list it names, so a receiver in a dead zone
  // still reads "From Almaty" under the cached transfers. A device that never
  // loaded it leaves the name out rather than print an id.
  const facilities = useCachedRead<S["FacilityListResponse"]>({
    queryKey: ["floor", "facilities", "active"],
    cacheKey: readCacheKey("facilities", "active"),
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/org/facilities", {
          params: { query: { status: "active" } },
          signal: floorReadSignal(signal),
        }),
        "facilities",
      ),
  });
  const facilityName = (id: string): string | undefined =>
    facilities.data?.items.find((f) => f.id === id)?.name;
  const read = useCachedRead<S["TransferListResponse"]>({
    queryKey: ["floor", "transfers", "inbound"],
    cacheKey: readCacheKey("transfers", "inbound"),
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/inventory/transfers", {
          params: { query: { status: "dispatched", limit: 50 } },
          signal: floorReadSignal(signal),
        }),
        "transfers",
      ),
  });

  const transfers = read.data?.items ?? [];

  return (
    <FloorScreen
      wide
      title={t("inventory.floor.receive.listTitle")}
      back={
        <Link to="/floor" className="floor-back">
          {t("inventory.floor.back")}
        </Link>
      }
    >
      <SnapshotNote
        source={read.source}
        cachedAt={read.cachedAt}
        revalidating={read.revalidating}
      />
      {read.isLoading ? <FloorSkeleton rows={3} /> : null}

      {/* A read that did not come back is NOT an empty dock. `source === "none"`
          means no live answer and nothing cached: online that is a failed
          request (error + retry + support code), offline it is a device that has
          never loaded the inbound list. Without this branch a 500 renders as
          "nothing on its way" and a receiver walks away from a loaded truck.
          Mirrors the manifest half below. */}
      {read.source === "none" && !read.isLoading ? (
        read.online ? (
          <FloorError
            title={t("inventory.floor.receive.listLoadFailedTitle")}
            detail={t("inventory.floor.receive.listLoadFailedBody")}
            requestId={floorRequestId(read.error)}
            onRetry={read.refetch}
          />
        ) : (
          <FloorNeedsConnection what={t("inventory.floor.receive.thisInbound")} />
        )
      ) : null}

      {!read.isLoading && read.source !== "none" && transfers.length === 0 ? (
        <FloorEmpty
          title={t("inventory.floor.receive.emptyTitle")}
          body={
            read.online
              ? t("inventory.floor.receive.emptyBody")
              : t("inventory.floor.receive.emptyOfflineBody")
          }
        />
      ) : null}

      <ul className="floor-cards" data-testid="floor-inbound-list">
        {transfers.map((transfer) => {
          const from = facilityName(transfer.from_facility_id);
          return (
            <li key={transfer.id}>
              {/* The whole card opens the transfer (D-444 ruling 6), and it says
                  what a receiver needs before walking to the dock: where it
                  comes from, when it is due and who brings it. */}
              <Link
                to="/floor/receive/$id"
                params={{ id: transfer.id }}
                className="floor-card floor-card-link"
                data-testid="floor-inbound-open"
              >
                <span className="floor-card-title">{transfer.transfer_code}</span>
                {from !== undefined ? (
                  <span className="floor-card-sub">{t("home.floor.from", { name: from })}</span>
                ) : null}
                <span className="floor-card-meta">
                  {transfer.eta_on !== null
                    ? t("home.floor.due", {
                        date: formatBusinessDate(transfer.eta_on, i18n.language),
                      })
                    : null}
                  {transfer.eta_on !== null ? " · " : null}
                  {transfer.carrier ?? t("inventory.floor.receive.noCarrier")}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </FloorScreen>
  );
}

// ---------------------------------------------------------------------------
// One transfer
// ---------------------------------------------------------------------------

export interface LineDraft {
  /** Unit lines: ticked = received in full. Bulk lines: unused. */
  readonly ticked: boolean;
  readonly qty: string;
  readonly reason: string;
}

export const EMPTY_DRAFT: LineDraft = { ticked: false, qty: "", reason: "" };

export interface ReceivePlan {
  readonly payload: TransferReceiveRequest["lines"];
  /** A short line with no reason — 422 TRANSFER_VARIANCE_REASON_REQUIRED (§3.3). */
  readonly needsReason: boolean;
  /** Past the dispatched quantity — 422 TRANSFER_STATE_INVALID (found excess
   * is a §3.9 adjustment, not a receive). */
  readonly overReceive: boolean;
  readonly malformed: boolean;
}

/**
 * Turn the operator's ticks and numbers into the §5 row 35 request body, or
 * into the reason it cannot be sent yet.
 *
 * PURE, and exported, because this is where every §3.3 wire rule is enforced
 * and a rule enforced only inside a render is a rule nobody can test. The three
 * refusal flags are computed rather than discovered from a server round trip: a
 * body that provably cannot land must never enter the outbox, where it would
 * sit until the operator is off shift.
 */
/**
 * How much of a line is OUTSTANDING, in the LINE's own UoM.
 *
 * The trap this exists to close: `quantity` is expressed in `line.uom`, while
 * `qty_base` and `received_qty_base` are in the product's BASE UoM (contracts
 * `TransferLine`). Comparing an entered quantity against
 * `quantity - received_qty_base` therefore subtracts kilograms from metres for
 * every line whose UoM is not the base one — blocking legal receives, demanding
 * a variance reason for a full one, and breaking §4.4's "a second top-up
 * completes the line".
 *
 * The conversion is the line's own exact ratio `quantity / qty_base` applied to
 * the received base amount, done in scaled integers (D-042 — never a float
 * ratio on a ledger quantity). When `qty_base` is zero the ratio does not
 * exist; the local check then declines to guess and the server decides.
 */
export function outstandingInLineUom(line: TransferLine): bigint | undefined {
  const dispatched = parseMeasure(line.quantity);
  const base = parseMeasure(line.qty_base);
  const receivedBase = parseMeasure(line.received_qty_base);
  if (dispatched === undefined || base === undefined || receivedBase === undefined) {
    return undefined;
  }
  if (receivedBase === 0n) return dispatched;
  if (base <= 0n) return undefined;
  // received_in_line_uom = received_base * (quantity / qty_base), scaled ints.
  const receivedInLineUom = (receivedBase * dispatched) / base;
  const outstanding = dispatched - receivedInLineUom;
  return outstanding > 0n ? outstanding : 0n;
}

export function planReceive(
  lines: readonly TransferLine[],
  draftOf: (line: TransferLine) => LineDraft,
): ReceivePlan {
  const payload: TransferReceiveRequest["lines"] = [];
  let needsReason = false;
  let overReceive = false;
  let malformed = false;

  for (const line of lines) {
    const draft = draftOf(line);
    const isUnitLine = line.stock_unit_id !== null;
    const outstanding = outstandingInLineUom(line);

    if (isUnitLine) {
      // Whole or nothing (ruled 2d — receive whole, then re-measure).
      // A line the operator is simply NOT receiving needs no reason and no
      // input: §3.3/§9.10 say an unnamed unit line "is simply never named and
      // stays in_transit", and the server only requires a variance reason for a
      // line that IS named and short. Demanding a dropdown whose value is then
      // discarded blocked Confirm on a field the wire never carried.
      if (!draft.ticked) continue;
      payload.push({ line_id: line.id, received_qty: line.quantity });
      continue;
    }

    const text = draft.qty.trim();
    if (text.length === 0) continue;
    if (!MEASURE_RE.test(text)) {
      malformed = true;
      continue;
    }
    const entered = parseMeasure(text) ?? 0n;
    if (entered <= 0n) {
      malformed = true;
      continue;
    }
    if (outstanding === undefined) {
      // No exact ratio: send it and let the server be the authority rather than
      // refuse on arithmetic we cannot do honestly.
      payload.push({
        line_id: line.id,
        received_qty: text,
        ...(draft.reason.trim().length > 0 ? { variance_reason: draft.reason.trim() } : {}),
      });
      continue;
    }
    if (entered > outstanding) {
      overReceive = true;
      continue;
    }
    const short = entered < outstanding;
    if (short && draft.reason.trim().length === 0) {
      needsReason = true;
      continue;
    }
    payload.push({
      line_id: line.id,
      received_qty: text,
      ...(short ? { variance_reason: draft.reason.trim() } : {}),
    });
  }
  return { payload, needsReason, overReceive, malformed };
}

interface ReceiveSnapshot {
  readonly transfer: S["InventoryTransfer"];
  readonly lines: readonly TransferLine[];
  readonly unitSerials: Readonly<Record<string, string>>;
  readonly productNames: Readonly<Record<string, string>>;
}

async function loadTransfer(id: string, signal?: AbortSignal): Promise<ReceiveSnapshot> {
  const detail = unwrap(
    await client.GET("/v1/inventory/transfers/{id}", {
      params: { path: { id } },
      signal: floorReadSignal(signal),
    }),
    "transfer",
  );
  const unitIds = [
    ...new Set(detail.lines.map((l) => l.stock_unit_id).filter((v): v is string => v !== null)),
  ];
  const productIds = [
    ...new Set(detail.lines.map((l) => l.product_id).filter((v): v is string => v !== null)),
  ];
  const unitSerials: Record<string, string> = {};
  const productNames: Record<string, string> = {};
  await Promise.all([
    ...unitIds.map(async (uid) => {
      try {
        const card = unwrap(
          await client.GET("/v1/inventory/stock-units/{id}", {
            params: { path: { id: uid } },
            signal: floorReadSignal(signal),
          }),
          "stock-unit",
        );
        unitSerials[uid] = card.unit.serial_code;
      } catch {
        /* short id chip */
      }
    }),
    ...productIds.map(async (pid) => {
      try {
        const product = unwrap(
          await client.GET("/v1/catalogue/products/{id}", {
            params: { path: { id: pid } },
            signal: floorReadSignal(signal),
          }),
          "product",
        );
        productNames[pid] = `${product.sku} · ${product.name}`;
      } catch {
        /* short id chip */
      }
    }),
  ]);
  return { transfer: detail.transfer, lines: detail.lines, unitSerials, productNames };
}

export function FloorReceiveScreen() {
  const { t, i18n } = useTranslation();
  const { id } = useParams({ from: "/floor/receive/$id" });
  const perms = useInventoryPermissions();
  const { state, submit, reset } = useFloorSubmit();
  // The ticks and numbers accumulated over a twenty-minute unload survive an
  // OS tab kill and a back-swipe. The outbox was already kill-safe; the hour of
  // typing in front of it was not.
  const drafts = useFloorDraft<Record<string, LineDraft>>(`transfer:${id}`, {});
  const [scanNote, setScanNote] = useState<string | undefined>(undefined);
  const [scanHit, setScanHit] = useState<string | undefined>(undefined);

  const read = useCachedRead<ReceiveSnapshot>({
    queryKey: ["floor", "transfer", id],
    cacheKey: readCacheKey("transfer", id),
    queryFn: ({ signal }) => loadTransfer(id, signal),
  });

  const snapshot = read.data;
  const lines = snapshot?.lines ?? [];

  function draftFor(line: TransferLine): LineDraft {
    return drafts.value[line.id] ?? EMPTY_DRAFT;
  }

  function update(lineId: string, patch: Partial<LineDraft>): void {
    drafts.set((prev) => ({
      ...prev,
      [lineId]: { ...(prev[lineId] ?? EMPTY_DRAFT), ...patch },
    }));
  }

  /** The lines this confirm will send, plus why it may not be sendable yet. */
  const plan = useMemo(
    () => planReceive(lines, (line) => drafts.value[line.id] ?? EMPTY_DRAFT),
    [lines, drafts.value],
  );

  const tickedCount = lines.filter((line) => {
    const draft = drafts.value[line.id] ?? EMPTY_DRAFT;
    return line.stock_unit_id !== null ? draft.ticked : draft.qty.trim().length > 0;
  }).length;

  const settlement = state.phase === "settled" ? state.settlement : undefined;
  const applied =
    settlement?.outcome === "applied"
      ? (settlement.response as TransferReceiveResponse)
      : undefined;

  const canConfirm =
    plan.payload.length > 0 &&
    !plan.needsReason &&
    !plan.overReceive &&
    !plan.malformed &&
    state.phase !== "submitting";

  return (
    <FloorScreen
      title={snapshot?.transfer.transfer_code ?? t("inventory.floor.receive.title")}
      back={
        <Link to="/floor/receive" className="floor-back">
          {t("inventory.floor.back")}
        </Link>
      }
      actions={
        snapshot !== undefined ? (
          <FloorButton
            variant="primary"
            disabled={!canConfirm}
            onClick={() => {
              const body: TransferReceiveRequest = { lines: plan.payload };
              void submit({ kind: "transfer_receive", transferId: id, body }, undefined, {
                identity: snapshot.transfer.transfer_code,
              }).then((settled) => {
                if (settled === undefined) {
                  floorFeedback("error");
                  return;
                }
                floorFeedback(settled.outcome === "needs_attention" ? "error" : "ok");
                // Confirmed work must not stay in the form: a re-tap would be a
                // SECOND receipt, and the queue deliberately never collapses two.
                if (settled.outcome === "applied" || settled.outcome === "queued") drafts.clear();
              });
            }}
            testId="floor-receive-confirm"
          >
            {/* The blocker rides the LABEL. A greyed-out bar whose explanation
                is above the fold is a bar the operator cannot act on. */}
            {plan.malformed
              ? t("inventory.floor.receive.blockedMalformed")
              : plan.overReceive
                ? t("inventory.floor.receive.blockedOver")
                : plan.needsReason
                  ? t("inventory.floor.receive.blockedReason")
                  : t("inventory.floor.receive.confirm", { count: plan.payload.length })}
          </FloorButton>
        ) : undefined
      }
    >
      <SnapshotNote
        source={read.source}
        cachedAt={read.cachedAt}
        revalidating={read.revalidating}
      />
      {read.isLoading ? <FloorSkeleton rows={4} /> : null}

      {read.source === "none" && !read.isLoading ? (
        read.online ? (
          <FloorError
            title={t("inventory.floor.receive.loadFailedTitle")}
            detail={t("inventory.floor.receive.loadFailedBody")}
            requestId={floorRequestId(read.error)}
            onRetry={read.refetch}
          />
        ) : (
          <FloorNeedsConnection what={t("inventory.floor.receive.thisManifest")} />
        )
      ) : null}

      {snapshot !== undefined ? (
        <>
          {!perms.has("inventory.transfer.receive") && perms.isResolved && !perms.isFeedError ? (
            <p className="floor-hint floor-hint-warn" data-testid="floor-receive-nopermission">
              {t("inventory.floor.receive.noPermission")}
            </p>
          ) : null}

          {snapshot.transfer.status !== "dispatched" &&
          snapshot.transfer.status !== "partially_received" ? (
            <p className="floor-hint floor-hint-warn" data-testid="floor-receive-wrong-state">
              {t("inventory.floor.receive.wrongState", {
                status: enumLabel(t, "inventory.vocab.transfer", snapshot.transfer.status),
              })}
            </p>
          ) : null}

          {lines.length === 0 ? (
            <FloorEmpty
              title={t("inventory.floor.receive.noLinesTitle")}
              body={t("inventory.floor.receive.noLinesBody")}
            />
          ) : null}

          {/* THE PRIMARY MOTION of §4.4 sits above the manifest, not below it.
              A hit used to change nothing but a tick twenty rows off-screen, so
              a good scan and a scanner that never fired were indistinguishable.
              Now the panel is the first thing on screen, a hit names what it
              ticked, and the running tally answers "how many left" without
              scrolling. Matching is LOCAL against the manifest — no round trip,
              works with no signal. */}
          <section className="floor-panel" data-testid="floor-receive-scan-panel">
            <h2 className="floor-panel-head">{t("inventory.floor.receive.scanHead")}</h2>
            <ScanTick
              snapshot={snapshot}
              onTick={(lineId, name) => {
                update(lineId, { ticked: true });
                setScanNote(undefined);
                setScanHit(name);
                floorFeedback("ok");
              }}
              onMiss={(payload) => {
                setScanHit(undefined);
                setScanNote(t("inventory.floor.receive.scanNoMatch", { payload }));
                floorFeedback("error");
              }}
            />
            {scanHit !== undefined ? (
              <p className="floor-result-ok" role="status" data-testid="floor-receive-scan-hit">
                {t("inventory.floor.receive.scanTicked", {
                  name: scanHit,
                  done: tickedCount,
                  total: lines.length,
                })}
              </p>
            ) : null}
            {scanNote !== undefined ? (
              <p className="floor-scan-error" role="alert" data-testid="floor-receive-scan-miss">
                {scanNote}
              </p>
            ) : null}
            <p className="floor-progress" data-testid="floor-receive-progress">
              {t("inventory.floor.receive.progress", { done: tickedCount, total: lines.length })}
            </p>
          </section>

          <ul className="floor-lines" data-testid="floor-receive-lines">
            {lines.map((line) => {
              const draft = draftFor(line);
              const isUnitLine = line.stock_unit_id !== null;
              const label =
                (line.stock_unit_id !== null
                  ? snapshot.unitSerials[line.stock_unit_id]
                  : undefined) ??
                (line.product_id !== null ? snapshot.productNames[line.product_id] : undefined) ??
                t("inventory.floor.receive.unnamedLine", { n: line.line_no });
              const entered = parseMeasure(draft.qty.trim()) ?? 0n;
              const outstanding = outstandingInLineUom(line);
              // A unit line the operator has not ticked is NOT short — it is
              // simply not being received, and §9.10 says it stays in transit.
              const short =
                !isUnitLine &&
                draft.qty.trim().length > 0 &&
                outstanding !== undefined &&
                entered < outstanding;
              const over = !isUnitLine && outstanding !== undefined && entered > outstanding;
              const untouched = isUnitLine ? !draft.ticked : draft.qty.trim().length === 0;

              return (
                <li
                  key={line.id}
                  className={`floor-line ${draft.ticked || draft.qty.trim().length > 0 ? "floor-line-done" : ""}`}
                  data-testid="floor-receive-line"
                >
                  <p className="floor-line-title">
                    {draft.ticked || (!isUnitLine && draft.qty.trim().length > 0) ? (
                      <span className="floor-tick" aria-hidden="true" data-testid="floor-line-tick">
                        ✓
                      </span>
                    ) : null}
                    {label}
                  </p>
                  <p className="floor-line-sub">
                    {t("inventory.floor.receive.dispatched", {
                      qty: formatMeasure(line.quantity, { locale: i18n.language }),
                      uom: unitSymbol(line.uom, { locale: i18n.language }),
                    })}
                  </p>

                  {isUnitLine ? (
                    <label className="floor-check">
                      <input
                        type="checkbox"
                        checked={draft.ticked}
                        onChange={(event) => {
                          update(line.id, { ticked: event.target.checked });
                        }}
                        data-testid="floor-receive-tick"
                      />
                      <span>{t("inventory.floor.receive.tickWhole")}</span>
                    </label>
                  ) : (
                    <div className="floor-field">
                      <label htmlFor={`recv-${line.id}`}>
                        {t("inventory.floor.receive.qtyLabel", {
                          uom: unitSymbol(line.uom, { locale: i18n.language }),
                        })}
                      </label>
                      <input
                        id={`recv-${line.id}`}
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        value={draft.qty}
                        onChange={(event) => {
                          update(line.id, { qty: event.target.value });
                        }}
                        data-testid="floor-receive-qty"
                      />
                    </div>
                  )}

                  {over ? (
                    <p className="floor-hint floor-hint-warn" data-testid="floor-receive-over">
                      {t("inventory.floor.receive.overReceive")}
                    </p>
                  ) : null}

                  {/* §9.10: what is not named simply stays in transit. Stating
                      it is the whole job here — it is a fact, not an input. */}
                  {untouched ? (
                    <p className="floor-hint" data-testid="floor-receive-in-transit">
                      {t("inventory.floor.receive.staysInTransit")}
                    </p>
                  ) : null}

                  {short ? (
                    <div className="floor-field" data-testid="floor-receive-variance">
                      <label htmlFor={`reason-${line.id}`}>
                        {t("inventory.floor.receive.reasonLabel")}
                      </label>
                      <select
                        id={`reason-${line.id}`}
                        value={draft.reason}
                        onChange={(event) => {
                          update(line.id, { reason: event.target.value });
                        }}
                        data-testid="floor-receive-reason"
                      >
                        <option value="">{t("inventory.floor.receive.reasonPick")}</option>
                        {VARIANCE_REASONS.map((reason) => (
                          <option key={reason} value={reason}>
                            {t(`inventory.floor.receive.reason.${reason}`)}
                          </option>
                        ))}
                      </select>
                      <p className="floor-hint">{t("inventory.floor.receive.shortfallNote")}</p>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>

          {plan.needsReason ? (
            <p className="floor-hint floor-hint-warn" data-testid="floor-receive-needs-reason">
              {t("inventory.floor.receive.needsReason")}
            </p>
          ) : null}
          {plan.malformed ? (
            <p className="floor-scan-error" role="alert" data-testid="floor-receive-malformed">
              {t("inventory.floor.scan.measureInvalid")}
            </p>
          ) : null}

          {settlement?.outcome === "queued" ? (
            <p className="floor-result-queued" role="status" data-testid="floor-receive-queued">
              {t("inventory.floor.receive.queued")}
            </p>
          ) : null}
          {settlement?.outcome === "needs_attention" ? (
            <FloorError
              title={t("inventory.floor.receive.failedTitle")}
              detail={floorFailureMessage(t, settlement.failure)}
              requestId={settlement.failure.requestId}
            />
          ) : null}
          {applied !== undefined ? (
            <p className="floor-result-body" role="status" data-testid="floor-receive-done">
              {applied.complete
                ? t("inventory.floor.receive.doneComplete")
                : t("inventory.floor.receive.donePartial")}
            </p>
          ) : null}
          {state.phase === "not_saved" ? <FloorNotSaved onRetry={reset} /> : null}
        </>
      ) : null}
    </FloorScreen>
  );
}

function ScanTick({
  snapshot,
  onTick,
  onMiss,
}: {
  snapshot: ReceiveSnapshot;
  onTick: (lineId: string, name: string) => void;
  onMiss: (payload: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <ScanInput
      autoFocus
      captureWedge
      label={t("inventory.floor.receive.scanLabel")}
      placeholder={t("inventory.floor.receive.scanPlaceholder")}
      onScan={(payload) => {
        const needle = payload.trim().toLowerCase();
        const line = snapshot.lines.find((candidate) => {
          if (candidate.stock_unit_id === null) return false;
          const serial = snapshot.unitSerials[candidate.stock_unit_id];
          if (serial?.toLowerCase() === needle) return true;
          // A canonical QR carries the unit UUID (§9.9); the manifest holds it.
          return needle.includes(candidate.stock_unit_id.toLowerCase());
        });
        const unitId = line?.stock_unit_id ?? undefined;
        if (line === undefined || unitId === undefined) onMiss(payload);
        else onTick(line.id, snapshot.unitSerials[unitId] ?? payload);
      }}
    />
  );
}
